import { useNavigate } from "react-router-dom";
import { ChevronLeft, FileText } from "lucide-react";

const SECTIONS = [
  {
    title: "1. Acceptance of Terms",
    body: [
      'By accessing or using the Exqura platform (website and mobile application), you agree to be bound by these Terms of Service ("Terms") and our Privacy Policy. If you do not agree to these Terms, please do not use our Service.',
      "These Terms apply to all users of Exqura, including guests, registered users, and any person who browses or accesses the platform.",
    ],
  },
  {
    title: "2. About Exqura",
    body: [
      "Exqura Golf & Experiences is an online booking platform that enables users to discover and book golf courses, ferry travel, ground transportation, and accommodation — primarily for golf travel in Southeast Asia.",
      "Exqura acts as an intermediary between users and third-party service providers (golf courses, ferry operators, transport providers, and hotels). We are not the direct provider of these services.",
    ],
  },
  {
    title: "3. Eligibility",
    body: [
      "You must be at least 18 years old to create an account and make bookings on Exqura. By using the Service, you represent and warrant that you meet this age requirement.",
      "You are responsible for ensuring that your use of Exqura complies with all laws and regulations applicable in your jurisdiction.",
    ],
  },
  {
    title: "4. Account Registration",
    body: [
      "To access certain features, you must create an account. You agree to:",
      "• Provide accurate, current, and complete information during registration.",
      "• Keep your account credentials confidential and not share them with others.",
      "• Notify us immediately at support@exqura.com if you suspect unauthorised access to your account.",
      "• Be responsible for all activity that occurs under your account.",
      "Exqura reserves the right to suspend or terminate accounts that violate these Terms.",
    ],
  },
  {
    title: "5. Bookings and Payments",
    body: [
      "5.1 Booking Confirmation: A booking is confirmed only when you receive a confirmation email or in-app confirmation from Exqura. Availability is not guaranteed until confirmation is received.",
      "5.2 Pricing: All prices displayed are in the stated currency and are inclusive of applicable fees unless stated otherwise. Prices are subject to change until a booking is confirmed.",
      "5.3 Payment: Payments are processed securely through our payment partners. By completing a booking, you authorise Exqura to charge the stated amount to your selected payment method.",
      "5.4 Third-Party Providers: Certain services (e.g. specific golf courses, ferry operators) may have their own pricing, availability, and terms. Exqura is not responsible for changes imposed by third-party providers.",
    ],
  },
  {
    title: "6. Cancellations and Refunds",
    body: [
      "6.1 User-Initiated Cancellations: Cancellation policies vary by service provider. The applicable policy will be displayed before you confirm your booking.",
      "6.2 Free Cancellation: Where indicated, bookings may be cancelled free of charge up to 7 days before the travel date. Cancellations made after this window may incur fees.",
      "6.3 Refunds: Approved refunds will be processed to the original payment method within 7–14 business days, subject to the service provider's policy.",
      "6.4 Exqura-Initiated Changes: In the rare event that Exqura or a service provider must cancel a confirmed booking, we will notify you promptly and offer a full refund or alternative arrangement.",
    ],
  },
  {
    title: "7. User Conduct",
    body: [
      "You agree not to:",
      "• Use Exqura for any unlawful purpose or in violation of these Terms.",
      "• Submit false, misleading, or fraudulent information.",
      "• Attempt to gain unauthorised access to any part of the Service or its systems.",
      "• Scrape, copy, or reproduce any content from Exqura without prior written permission.",
      "• Post or transmit offensive, defamatory, or inappropriate content.",
      "• Interfere with or disrupt the integrity or performance of the Service.",
      "Violation of these rules may result in immediate account termination.",
    ],
  },
  {
    title: "8. Intellectual Property",
    body: [
      'All content on Exqura — including logos, text, graphics, images, and software — is the property of Exqura Golf & Experiences or its licensors and is protected by applicable intellectual property laws.',
      "You are granted a limited, non-exclusive, non-transferable licence to access and use the Service for personal, non-commercial purposes. You may not reproduce, distribute, or create derivative works from any content without our express written consent.",
    ],
  },
  {
    title: "9. Third-Party Services",
    body: [
      "Exqura integrates with third-party services including Facebook Login, payment processors, and booking partners. Your use of these services is subject to their respective terms and privacy policies.",
      "We are not responsible for the actions, content, or availability of third-party services. Links to external websites are provided for convenience only and do not imply endorsement.",
    ],
  },
  {
    title: "10. Limitation of Liability",
    body: [
      "To the maximum extent permitted by law:",
      "• Exqura provides the Service on an 'as is' and 'as available' basis without warranties of any kind.",
      "• We do not guarantee that the Service will be uninterrupted, error-free, or free from viruses.",
      "• Exqura is not liable for any indirect, incidental, special, or consequential damages arising from your use of the Service, including loss of bookings, travel disruptions, or data loss.",
      "• Our total liability to you for any claim arising from these Terms shall not exceed the amount paid by you for the relevant booking in the preceding 3 months.",
    ],
  },
  {
    title: "11. Indemnification",
    body: [
      "You agree to indemnify and hold harmless Exqura, its directors, employees, and partners from any claims, losses, liabilities, or expenses (including legal fees) arising from:",
      "• Your use of the Service.",
      "• Your violation of these Terms.",
      "• Your violation of any third party's rights.",
    ],
  },
  {
    title: "12. Privacy",
    body: [
      "Your use of Exqura is also governed by our Privacy Policy, which is incorporated into these Terms by reference. By using the Service, you consent to the collection and use of your information as described in the Privacy Policy.",
    ],
  },
  {
    title: "13. Governing Law",
    body: [
      "These Terms are governed by and construed in accordance with the laws of Singapore, without regard to its conflict of law provisions.",
      "Any disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of Singapore.",
    ],
  },
  {
    title: "14. Changes to These Terms",
    body: [
      "We may update these Terms from time to time. We will notify you of material changes by posting the revised Terms on this page with an updated effective date.",
      "Your continued use of Exqura after changes take effect constitutes your acceptance of the revised Terms. We recommend reviewing this page periodically.",
    ],
  },
  {
    title: "15. Termination",
    body: [
      "Exqura reserves the right to suspend or terminate your account and access to the Service at its sole discretion, without notice, for conduct that violates these Terms or is harmful to other users, third parties, or the integrity of the Service.",
      "You may close your account at any time by contacting us at support@exqura.com.",
    ],
  },
  {
    title: "16. Contact Us",
    body: [
      "If you have any questions about these Terms of Service, please contact us:",
      "Email: support@exqura.com",
      "Website: www.exqura.com",
      "Exqura Golf & Experiences",
      "Singapore",
    ],
  },
];

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#0B2421]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm mb-6 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center">
              <FileText className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-widest">Legal</p>
          </div>
          <h1 className="text-white font-bold text-3xl sm:text-4xl mb-2">Terms of Service</h1>
          <p className="text-white/50 text-sm">Effective date: 9 July 2026 &nbsp;·&nbsp; Last updated: 9 July 2026</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Intro */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8 mb-6">
          <p className="text-gray-600 leading-relaxed">
            Please read these Terms of Service carefully before using{" "}
            <span className="font-semibold text-[#0B2421]">Exqura Golf & Experiences</span>. These Terms govern your access to and use of our
            website, mobile application, and booking services. Using Exqura means you agree to these Terms.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {SECTIONS.map((section) => (
            <div key={section.title} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
              <h2 className="text-[#0B2421] font-bold text-lg mb-4">{section.title}</h2>
              <div className="space-y-2">
                {section.body.map((para, i) => (
                  <p key={i} className="text-gray-600 text-sm leading-relaxed">{para}</p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-400 text-xs mt-8 pb-8">
          © {new Date().getFullYear()} Exqura Golf & Experiences. All rights reserved.
        </p>
      </div>
    </div>
  );
}
